import { Component, OnInit, OnDestroy } from '@angular/core';
import { takeWhile } from 'rxjs';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';

import { doesRoleIncludes } from '@care-portals/shared';
import {
  CannedMessage,
  CannedMessageCategory,
  CannedMessageQuery,
  ILanguages,
  IMessageScope
} from '@care-portals/api-interfaces';
import { APIService } from '../../../services/api/api.service';
import { CannedMessagesFacade } from '../../../+store/canned-messages/canned-messages.facade';
import { AuthFacade } from '../../../+store/auth/auth.facade';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import { Validators } from '@angular/forms';
import { CANNED_MESSAGE_PLACEHOLDERS, USERS_ROLES } from '@care-portals/data';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { CommonModule } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { ScrollingModule } from '@angular/cdk/scrolling';

@Component({
  selector: 'portals-provider-canned-messages',
  templateUrl: './canned-messages.component.html',
  styleUrls: ['./canned-messages.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzFormModule,
    ReactiveFormsModule,
    NgxEditorModule,
    NzSelectModule,
    NzRadioModule,
    NzButtonModule,
    NzInputModule,
    NzTypographyModule,
    NzPopconfirmModule,
    ScrollingModule
  ]
})
export class CannedMessagesComponent implements OnInit, OnDestroy {
  isActive = false;
  cannedMessages = [];
  // Messages grouped by role for display (e.g. all Pharmacy ones, then Admin,
  // then untagged "General"). Rebuilt whenever the list or the role filter
  // changes.
  groupedMessages: { key: string; label: string; messages: CannedMessage[] }[] =
    [];
  selectedMessage: CannedMessage;
  editId;
  editMode: boolean = false;
  createMode: boolean = false;
  displayMessageScopes: (IMessageScope | 'roles')[] = [
    'organization',
    'group',
    'user',
    'roles'
  ];
  newMessageScopes: (IMessageScope | 'role')[];
  newMessageLanguages: ILanguages[] = ['en', 'fr'];
  selectedScope: IMessageScope | 'roles' | 'all' = 'roles';
  initialNewMessage: CannedMessage = {
    _id: '',
    scopeType: 'user',
    scopeKey: '',
    name: '',
    content: '',
    language: 'en',
    roles: [],
    category: ''
  };
  role: string;
  adminAccess: boolean = false;
  query: CannedMessageQuery = {
    scopeType: 'all',
    language: 'en'
  };
  categories: CannedMessageCategory[] = [];
  // Derived from the logged-in user's own role segments (see role$ below), so a
  // user only sees/manages the roles assigned to them.
  roleOptions: { value: string; label: string }[] = [];
  selectedCategory: string = '';
  selectedRoles: string[] = [];
  // Inline category management state.
  showManageCategories: boolean = false;
  newCategoryName: string = '';
  editingCategoryId: string | null = null;
  editCategoryName: string = '';
  // Inline "add category" from the create/edit form.
  showAddFormCategory: boolean = false;
  newFormCategoryName: string = '';
  // Persist the browse filter so the drawer reopens where the user left it.
  private readonly FILTER_STORAGE_KEY = 'cannedMessagesFilter';
  groups: string[] = [];
  userId: string;
  validateForm: FormGroup;
  editor: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['link'],
    ['ordered_list', 'bullet_list']
  ];
  placeholder;
  placeholderOptions = CANNED_MESSAGE_PLACEHOLDERS;
  selectedGroup: string;
  selectedLanguage: ILanguages = 'en';
  languages: ILanguages[] = ['en', 'fr'];
  newMessageScope: IMessageScope | 'role';

  constructor(
    private fb: FormBuilder,
    private facade: CannedMessagesFacade,
    private authFacade: AuthFacade,
    private apiService: APIService,
    private nzDrawerRef: NzDrawerRef
  ) {}

  ngOnInit() {
    this.isActive = true;

    // Restore the last-used browse filter (if any) so the drawer reopens where
    // the user left it, instead of always defaulting to the roles view.
    const saved = this.loadFilterState();

    this.authFacade.role$
      .pipe(takeWhile(() => this.isActive))
      .subscribe(role => {
        this.role = role;
        this.adminAccess = doesRoleIncludes(role, ['admin', 'owner']);
        this.newMessageScopes = this.adminAccess
          ? ['organization', 'group', 'user', 'role']
          : ['user'];

        // The roles a user can see/manage are exactly their own role segments
        // (e.g. `pharmacy-admin` -> ['pharmacy','admin']), labelled from the
        // canonical USERS_ROLES map. Powers both the filter and form dropdowns.
        const roleSegments = (role ? role.split('-') : []).filter(
          segment => !!USERS_ROLES[segment as keyof typeof USERS_ROLES]
        );
        this.roleOptions = roleSegments.map(segment => ({
          value: segment,
          label: USERS_ROLES[segment as keyof typeof USERS_ROLES].label
        }));

        // Create-form default: a new message's `roles` default to the author's
        // own segments (independent of the saved browse filter).
        this.initialNewMessage = {
          ...this.initialNewMessage,
          roles: [...roleSegments] as any
        };
        if (this.validateForm && !this.editMode) {
          this.validateForm.get('roles')?.setValue([...roleSegments]);
        }

        // Only default the browse filter to the user's own roles when there is
        // no saved filter to restore.
        if (!saved) {
          this.selectedRoles = roleSegments;
          this.query = { ...this.query, roles: this.selectedRoles as any };
        }
      });

    this.authFacade.user$
      .pipe(takeWhile(() => this.isActive))
      .subscribe(user => {
        this.groups = user.groups;
        this.userId = user._id;
      });

    this.facade.cannedMessages$
      .pipe(takeWhile(() => this.isActive))
      .subscribe(messages => {
        this.cannedMessages = messages;
        this.buildGroupedMessages();
      });

    this.editor = new Editor({});

    if (saved) {
      this.applySavedFilter(saved);
    }

    this.loadCannedMessages();
    this.loadCategories();

    this.validateForm = this.fb.group({
      name: ['', [Validators.required]],
      content: ['', [Validators.required]],
      scopeType: [this.initialNewMessage.scopeType, [Validators.required]],
      groupName: [''],
      language: [this.initialNewMessage.language, [Validators.required]],
      category: [this.initialNewMessage.category],
      roles: [this.initialNewMessage.roles],
      placeholder: ['']
    });

    this.validateForm
      .get('placeholder')
      ?.valueChanges.subscribe(placeholder => {
        if (placeholder) {
          this.insertPlaceholder(placeholder);
        }
      });

    this.validateForm.get('scopeType')?.valueChanges.subscribe(scope => {
      this.newMessageScope = scope;
      const groupNameControl = this.validateForm.get('groupName');
      if (scope === 'group') {
        groupNameControl.setValidators([Validators.required]);
      } else {
        groupNameControl.clearValidators();
        // Roles only apply to group- or role-scoped messages — clear otherwise.
        if (scope !== 'role') {
          this.validateForm.get('roles')?.setValue([]);
        }
      }
      groupNameControl.updateValueAndValidity();
    });
  }

  ngOnDestroy(): void {
    this.isActive = false;
    this.editor.destroy();
  }

  // Groups the current message list by role for display. Non-admin roles come
  // first (in filter-selection order), then `admin` (the broad catch-all role)
  // last, and finally untagged messages under "General". Each message appears
  // in only one group. With no role filter selected, the list is left ungrouped.
  buildGroupedMessages() {
    const roleLabel = (role: string) =>
      this.roleOptions.find(option => option.value === role)?.label ??
      USERS_ROLES[role as keyof typeof USERS_ROLES]?.label ??
      role;

    // Group by role whenever roles are selected (roles scope or group scope);
    // otherwise show a flat list.
    if (!this.selectedRoles?.length) {
      this.groupedMessages = this.cannedMessages.length
        ? [{ key: 'all', label: '', messages: this.cannedMessages }]
        : [];
      return;
    }

    // Keep `admin` last so pharmacy/provider messages surface above it.
    const orderedRoles = [
      ...this.selectedRoles.filter(role => role !== 'admin'),
      ...this.selectedRoles.filter(role => role === 'admin')
    ];

    const placed = new Set<string>();
    const groups: {
      key: string;
      label: string;
      messages: CannedMessage[];
    }[] = [];

    for (const role of orderedRoles) {
      const messages = this.cannedMessages.filter(
        message =>
          !placed.has(message._id) && (message.roles || []).includes(role)
      );
      messages.forEach(message => placed.add(message._id));
      if (messages.length) {
        groups.push({ key: role, label: roleLabel(role), messages });
      }
    }

    // Untagged messages are visible to everyone — show them in their own group.
    const general = this.cannedMessages.filter(
      message => !placed.has(message._id) && !(message.roles || []).length
    );
    general.forEach(message => placed.add(message._id));
    if (general.length) {
      groups.push({ key: 'general', label: 'General', messages: general });
    }

    this.groupedMessages = groups;
  }

  loadCannedMessages() {
    if (this.query.scopeType === 'user') {
      this.query.scopeKey = this.userId;
    } else if (this.query.scopeType === 'group') {
      this.query.scopeKey = this.selectedGroup;
    } else if (this.query.scopeType === 'organization') {
      this.query.scopeKey = '';
    }
    this.saveFilterState();
    this.facade.loadCannedMessages(this.query);
  }

  private loadFilterState(): {
    scope?: IMessageScope | 'roles' | 'all';
    roles?: string[];
    language?: ILanguages;
    category?: string;
    group?: string;
  } | null {
    try {
      const raw = localStorage.getItem(this.FILTER_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private saveFilterState() {
    try {
      localStorage.setItem(
        this.FILTER_STORAGE_KEY,
        JSON.stringify({
          scope: this.selectedScope,
          roles: this.selectedRoles,
          language: this.selectedLanguage,
          category: this.selectedCategory,
          group: this.selectedGroup
        })
      );
    } catch {
      // Ignore storage errors (e.g. storage disabled).
    }
  }

  private applySavedFilter(saved: {
    scope?: IMessageScope | 'roles' | 'all';
    roles?: string[];
    language?: ILanguages;
    category?: string;
    group?: string;
  }) {
    this.selectedScope = saved.scope ?? this.selectedScope;
    this.selectedLanguage = saved.language ?? this.selectedLanguage;
    this.selectedCategory = saved.category ?? '';
    this.selectedGroup = saved.group ?? '';

    const rolesApply =
      this.selectedScope === 'roles' || this.selectedScope === 'group';
    // Only keep roles when the scope uses them, and drop any the user no longer
    // has as an option.
    const allowedRoles = this.roleOptions.map(option => option.value);
    this.selectedRoles = rolesApply
      ? (saved.roles ?? []).filter(role => allowedRoles.includes(role))
      : [];
    this.query = {
      ...this.query,
      scopeType: (this.selectedScope === 'roles'
        ? 'all'
        : this.selectedScope) as any,
      language: this.selectedLanguage,
      category: this.selectedCategory || undefined,
      roles: (rolesApply ? this.selectedRoles : []) as any
    };
    if (this.selectedScope === 'group') {
      this.query.scopeKey = this.selectedGroup;
    }
  }

  saveMessage(id: string, message: any) {
    // "role" is a UI scope meaning org-wide but restricted to the selected
    // roles — store it as an organization-scoped message with those roles.
    if (message.scopeType === 'role') {
      message.scopeType = 'organization';
    }
    if (message.scopeType === 'user') {
      message.scopeKey = this.userId;
    } else if (message.scopeType === 'group') {
      message.scopeKey = this.validateForm.get('groupName').value;
    } else if (message.scopeType === 'organization') {
      message.scopeKey = '';
    }
    delete message.groupName;

    if (id) {
      message._id = id;
      this.facade.updateCannedMessage(message);
    } else {
      this.facade.addCannedMessage(message);
    }

    this.resetForm();
  }

  insertPlaceholder(placeholder: string) {
    const placeholderText = `{{ ${placeholder} }}`;

    if (this.editor) {
      const selection = this.editor.view.state.selection;

      if (selection) {
        const transaction = this.editor.view.state.tr.insertText(
          placeholderText,
          selection.from,
          selection.to
        );
        this.editor.view.dispatch(transaction);
      }
    }

    this.validateForm.get('placeholder').setValue('', { emitEvent: false });
  }

  resetForm() {
    this.validateForm.reset({
      ...this.initialNewMessage
    });

    this.editor.setContent('');

    this.editMode = false;
    this.createMode = false;
    this.editId = null;
  }

  openMessage(message: CannedMessage) {
    // If this message is currently being edited, a click cancels the edit and
    // returns to the normal row (name + preview) instead of expanding on top
    // of the edit form.
    if (this.editMode && this.editId === message._id) {
      this.resetForm();
      this.selectedMessage = null;
      return;
    }
    if (this.selectedMessage === message) {
      this.selectedMessage = null;
    } else {
      this.selectedMessage = message;
    }
  }

  // One-click insert: close with the message so the inbox applies it once.
  insertMessage(message: CannedMessage, event?: Event) {
    event?.stopPropagation();
    this.nzDrawerRef.close(message);
  }

  // Replace raw `{{ token }}` merge fields with a friendly bracketed label
  // (e.g. `[Customer First Name]`) so they don't read like broken text.
  prettyPlaceholders(text: string): string {
    if (!text) return '';
    return text.replace(/{{\s*(\w+)\s*}}/g, (match, key) => {
      const option = this.placeholderOptions.find(o => o.value === key);
      return option ? `[${option.label}]` : match;
    });
  }

  // Plain-text, single-line preview of a message's rich-text content for the list.
  contentPreview(content: string): string {
    if (!content) return '';
    const text = content
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const pretty = this.prettyPlaceholders(text);
    return pretty.length > 90 ? `${pretty.slice(0, 90)}…` : pretty;
  }

  // Rich-text content for the expanded view, with placeholders shown as labels.
  displayContent(content: string): string {
    return this.prettyPlaceholders(content || '');
  }

  editMessage(message: CannedMessage) {
    this.editMode = true;
    this.createMode = false;
    this.editId = message._id;
    this.selectedMessage = null;
    // Org-wide messages that carry role tags map back to the "role" UI scope;
    // group messages need their group prefilled into the groupName control.
    const uiScope =
      message.scopeType === 'organization' && message.roles?.length
        ? 'role'
        : message.scopeType;
    this.validateForm.patchValue({
      ...message,
      scopeType: uiScope,
      groupName: message.scopeType === 'group' ? message.scopeKey : ''
    });
  }

  onScopeSelect(scope: IMessageScope | 'roles' | 'all') {
    this.selectedScope = scope;

    // "roles" is a filter mode rather than a stored scope: show messages across
    // all scopes, narrowed by the roles picked in the secondary dropdown.
    if (scope === 'roles') {
      this.query = {
        ...this.query,
        scopeType: 'all',
        roles: this.selectedRoles as any
      };
      this.loadCannedMessages();
      return;
    }

    // Group scope keeps the role filter active so you can view a group's
    // messages narrowed by role (the one useful role + scope combination).
    if (scope === 'group') {
      this.query = {
        ...this.query,
        scopeType: 'group',
        roles: this.selectedRoles as any
      };
      this.selectedGroup = '';
      this.cannedMessages = [];
      this.buildGroupedMessages();
      return;
    }

    // organization / user: ownership only — clear the role filter and selection
    // so nothing carries over from the previous scope.
    this.selectedRoles = [];
    this.query = { ...this.query, scopeType: scope, roles: [] as any };
    this.loadCannedMessages();
  }

  onLanguageSelect(language: ILanguages) {
    this.query = { ...this.query, language };
    this.loadCannedMessages();
  }

  onCategorySelect(category: string) {
    // Empty selection clears the category filter.
    this.query = { ...this.query, category: category || undefined };
    this.loadCannedMessages();
  }

  onRolesSelect(roles: string[]) {
    this.selectedRoles = roles;
    this.query = { ...this.query, roles: (roles || []) as any };
    this.loadCannedMessages();
  }

  loadCategories() {
    this.apiService
      .getCannedMessageCategories()
      .subscribe(categories => (this.categories = categories || []));
  }

  addCategory() {
    const name = (this.newCategoryName || '').trim();
    if (!name) return;
    this.apiService.addCannedMessageCategory(name).subscribe(() => {
      this.newCategoryName = '';
      this.loadCategories();
    });
  }

  // Add a category from within the create/edit form and select it immediately.
  addFormCategory() {
    const name = (this.newFormCategoryName || '').trim();
    if (!name) return;
    this.apiService.addCannedMessageCategory(name).subscribe(() => {
      this.apiService.getCannedMessageCategories().subscribe(categories => {
        this.categories = categories || [];
        this.validateForm.get('category')?.setValue(name);
      });
      this.newFormCategoryName = '';
      this.showAddFormCategory = false;
    });
  }

  startEditCategory(category: CannedMessageCategory) {
    this.editingCategoryId = category._id;
    this.editCategoryName = category.name;
  }

  cancelEditCategory() {
    this.editingCategoryId = null;
    this.editCategoryName = '';
  }

  saveCategory(category: CannedMessageCategory) {
    const name = (this.editCategoryName || '').trim();
    if (!name || name === category.name) {
      this.cancelEditCategory();
      return;
    }
    this.apiService
      .updateCannedMessageCategory(category._id, name)
      .subscribe(() => {
        // A rename propagates to messages server-side; keep the current filter
        // in sync and refresh both lists.
        if (this.selectedCategory === category.name) {
          this.selectedCategory = name;
          this.query = { ...this.query, category: name };
        }
        this.cancelEditCategory();
        this.loadCategories();
        this.loadCannedMessages();
      });
  }

  deleteCategory(category: CannedMessageCategory) {
    this.apiService
      .deleteCannedMessageCategory(category._id)
      .subscribe(() => {
        // Deleting clears the category from messages server-side; drop the
        // filter if it pointed at this category and refresh.
        if (this.selectedCategory === category.name) {
          this.selectedCategory = '';
          this.query = { ...this.query, category: undefined };
        }
        this.loadCategories();
        this.loadCannedMessages();
      });
  }

  onGroupSelect(group: string) {
    this.query = {
      ...this.query,
      scopeKey: group
    };
    this.loadCannedMessages();
  }

  openCreateMode() {
    // Clear any in-progress edit so the inline edit form and the create form
    // can't both render at once.
    this.resetForm();
    this.createMode = true;
  }

  deleteMessage(id: string) {
    this.facade.deleteCannedMessage(id);
    this.selectedMessage = null;
  }
}
